import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { FadeUp } from "../components/Reveal";
import { EmSwash } from "../components/EmSwash";
import { LegalContactCard, formatLegalDate } from "../components/LegalContactCard";
import { LegalToc, LegalTocMobile } from "../components/LegalToc";
import { CreateCampaign } from "../components/CreateCampaign";
import { DonateComingSoonDialog } from "../components/DonateComingSoonDialog";
import { SEO } from "../lib/SEO";
import { openPreferences } from "../lib/consent";

// Build-time date from this file's last git commit (today while it has
// uncommitted edits) — see craco.config.js. Edit the policy, the date moves.
const PRIVACY_LAST_UPDATED = formatLegalDate(process.env.REACT_APP_PRIVACY_UPDATED);

/**
 * Privacy Policy — legal boilerplate rendered in the site's editorial
 * type system. Copy is authoritative (do not edit prose without legal
 * review); layout is presentational only.
 */

// Small helpers for consistent legal typography inside the page.
const H2 = ({ children, id }) => (
  <h2
    id={id}
    className="mt-16 scroll-mt-28 border-t border-border pt-10 font-serif text-3xl leading-tight tracking-tight text-ink"
  >
    {children}
  </h2>
);

const H3 = ({ children, id }) => (
  <h3
    id={id}
    className="mt-10 text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine"
  >
    {children}
  </h3>
);

const P = ({ children }) => (
  <p className="mt-4 text-base leading-relaxed text-ink/85">{children}</p>
);

const UL = ({ children }) => (
  <ul className="mt-4 list-disc space-y-2 pl-6 text-base leading-relaxed text-ink/85 marker:text-tangerine">
    {children}
  </ul>
);

const Term = ({ children }) => (
  <span className="font-semibold text-ink">{children}</span>
);

const Category = ({ label, examples, collected }) => (
  <div className="mt-5 rounded-md border border-border bg-bone px-5 py-4">
    <p className="font-serif text-3xl leading-snug text-ink">{label}</p>
    <p className="mt-1.5 text-base leading-relaxed text-olive">
      <Term>Examples:</Term> {examples}
    </p>
    <p className="mt-1.5 text-[13px] font-semibold uppercase tracking-[0.15em]">
      <span className="text-olive/70">Collected:</span>{" "}
      <span className={collected === "Yes" ? "text-grass" : "text-scarlet"}>
        {collected}
      </span>
    </p>
  </div>
);

export default function PrivacyPolicy() {
  const [createOpen, setCreateOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  return (
    <div className="min-h-screen bg-sand">
      <SEO
        title="Privacy Policy — My Rich Aunt"
        description="How My Rich Aunt collects, uses, and protects donor and student data. Our commitment to privacy, GDPR + CCPA compliance."
        path="/privacy"
      />
      <Navbar onStart={() => setCreateOpen(true)} onDonate={() => setDonateOpen(true)} />

      <main className="mx-auto max-w-6xl px-6 py-16 lg:px-8 lg:py-24 xl:grid xl:grid-cols-[13rem_minmax(0,48rem)] xl:justify-center xl:gap-16">
        <LegalToc testid="privacy-toc" />
        <article id="legal-article" className="min-w-0 max-w-3xl">
        <FadeUp>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.2em] text-olive transition-colors hover:text-tangerine"
            data-testid="privacy-back-home"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
            Back home
          </Link>

          <span className="mt-8 block text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">
            Legal
          </span>
          <h1 className="mt-4 font-serif text-3xl lg:text-[3.5rem] leading-[0.95] tracking-tight text-ink" data-testid="privacy-heading">
            Privacy <EmSwash>Policy.</EmSwash>
          </h1>
          <p className="mt-5 text-[13px] font-medium uppercase tracking-[0.16em] text-olive" data-testid="privacy-last-updated">
            Last updated: {PRIVACY_LAST_UPDATED}
          </p>
          <LegalContactCard topic="your privacy" testid="privacy-contact-card" />
          <LegalTocMobile testid="privacy-toc-mobile" />

          <P>
            This Privacy Policy describes Our policies and procedures on the
            collection, use and disclosure of Your information when You use the
            Service and tells You about Your privacy rights and how the law
            protects You.
          </P>
          <P>
            We use Your Personal data to provide and improve the Service. By
            using the Service, You agree to the collection and use of
            information in accordance with this Privacy Policy.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="interpretation-and-definitions">Interpretation and Definitions</H2>

          <H3>Interpretation</H3>
          <P>
            The words of which the initial letter is capitalized have meanings
            defined under the following conditions. The following definitions
            shall have the same meaning regardless of whether they appear in
            singular or in plural.
          </P>

          <H3>Definitions</H3>
          <P>For the purposes of this Privacy Policy:</P>

          <UL>
            <li>
              <Term>Account</Term> means a unique account created for You to
              access our Service or parts of our Service.
            </li>
            <li>
              <Term>Affiliate</Term> means an entity that controls, is
              controlled by or is under common control with a party, where
              "control" means ownership of 50% or more of the shares, equity
              interest or other securities entitled to vote for election of
              directors or other managing authority.
            </li>
            <li>
              <Term>Business</Term>, for the purpose of CCPA/CPRA, refers to
              the Company as the legal entity that collects Consumers' personal
              information and determines the purposes and means of the
              processing of Consumers' personal information, or on behalf of
              which such information is collected and that alone, or jointly
              with others, determines the purposes and means of the processing
              of consumers' personal information, that does business in the
              State of California.
            </li>
            <li>
              <Term>CCPA and/or CPRA</Term> refers to California Consumer
              Privacy Act (the "CCPA") as amended by the California Privacy
              Rights Act of 2020 (the "CPRA").
            </li>
            <li>
              <Term>Company</Term> (referred to as either "the Company", "We",
              "Us" or "Our" in this Agreement) refers to My Rich Aunt Inc., 445
              North Rossmore Avenue, Suite 121.
            </li>
            <li>
              For the purpose of the GDPR, the Company is the Data Controller.
            </li>
            <li>
              <Term>Consumer</Term>, for the purpose of the CCPA/CPRA, means a
              natural person who is a California resident. A resident, as
              defined in the law, includes (1) every individual who is in the
              USA for other than a temporary or transitory purpose, and (2)
              every individual who is domiciled in the USA who is outside the
              USA for a temporary or transitory purpose.
            </li>
            <li>
              <Term>Cookies</Term> are small files that are placed on Your
              computer, mobile device or any other device by a website,
              containing the details of Your browsing history on that website
              among its many uses.
            </li>
            <li>
              <Term>Country</Term> refers to: California, United States
            </li>
            <li>
              <Term>Data Controller</Term>, for the purposes of the GDPR
              (General Data Protection Regulation), refers to the Company as
              the legal person which alone or jointly with others determines
              the purposes and means of the processing of Personal Data.
            </li>
            <li>
              <Term>Device</Term> means any device that can access the Service
              such as a computer, a cellphone or a digital tablet.
            </li>
            <li>
              <Term>Do Not Track (DNT)</Term> is a concept that has been
              promoted by US regulatory authorities, in particular the U.S.
              Federal Trade Commission (FTC), for the Internet industry to
              develop and implement a mechanism for allowing internet users to
              control the tracking of their online activities across websites.
            </li>
            <li>
              <Term>GDPR</Term> refers to EU General Data Protection Regulation.
            </li>
            <li>
              <Term>Personal Data</Term> is any information that relates to an
              identified or identifiable individual.
            </li>
            <li>
              For the purposes of GDPR, Personal Data means any information
              relating to You such as a name, an identification number,
              location data, online identifier or to one or more factors
              specific to the physical, physiological, genetic, mental,
              economic, cultural or social identity.
            </li>
            <li>
              For the purposes of the CCPA/CPRA, Personal Data means any
              information that identifies, relates to, describes or is capable
              of being associated with, or could reasonably be linked, directly
              or indirectly, with You.
            </li>
            <li>
              <Term>Service</Term> refers to the Website.
            </li>
            <li>
              <Term>Service Provider</Term> means any natural or legal person
              who processes the data on behalf of the Company. It refers to
              third-party companies or individuals employed by the Company to
              facilitate the Service, to provide the Service on behalf of the
              Company, to perform services related to the Service or to assist
              the Company in analyzing how the Service is used. For the purpose
              of the GDPR, Service Providers are considered Data Processors.
            </li>
            <li>
              <Term>Usage Data</Term> refers to data collected automatically,
              either generated by the use of the Service or from the Service
              infrastructure itself (for example, the duration of a page visit).
            </li>
            <li>
              <Term>Website</Term> refers to My Rich Aunt, accessible from{" "}
              <a
                href="http://www.myrichaunt.org"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-tangerine underline underline-offset-4 hover:text-cobalt"
              >
                http://www.myrichaunt.org
              </a>
            </li>
            <li>
              <Term>You</Term> means the individual accessing or using the
              Service, or the company, or other legal entity on behalf of which
              such individual is accessing or using the Service, as applicable.
            </li>
            <li>
              Under GDPR, You can be referred to as the Data Subject or as the
              User as you are the individual using the Service.
            </li>
          </UL>
        </FadeUp>

        <FadeUp>
          <H2 id="collecting-and-using-your-personal-data">
            Collecting and Using Your Personal Data
          </H2>

          <H3>Types of Data Collected</H3>

          <p className="mt-6 font-serif text-3xl leading-snug text-ink">
            Personal Data
          </p>
          <P>
            While using Our Service, We may ask You to provide Us with certain
            personally identifiable information that can be used to contact or
            identify You. Personally identifiable information may include, but
            is not limited to:
          </P>
          <UL>
            <li>Email address</li>
            <li>First name and last name</li>
            <li>Phone number</li>
            <li>Address, State, Province, ZIP/Postal code, City</li>
            <li>Usage Data</li>
          </UL>

          <p className="mt-8 font-serif text-3xl leading-snug text-ink">Usage Data</p>
          <P>Usage Data is collected automatically when using the Service.</P>
          <P>
            Usage Data may include information such as Your Device's Internet
            Protocol address (e.g. IP address), browser type, browser version,
            the pages of our Service that You visit, the time and date of Your
            visit, the time spent on those pages, unique device identifiers and
            other diagnostic data.
          </P>
          <P>
            When You access the Service by or through a mobile device, We may
            collect certain information automatically, including, but not
            limited to, the type of mobile device You use, Your mobile device
            unique ID, the IP address of Your mobile device, Your mobile
            operating system, the type of mobile Internet browser You use,
            unique device identifiers and other diagnostic data.
          </P>
          <P>
            We may also collect information that Your browser sends whenever
            You visit our Service or when You access the Service by or through
            a mobile device.
          </P>

          <p className="mt-8 font-serif text-3xl leading-snug text-ink">
            Tracking Technologies and Cookies
          </p>
          <P>
            We use Cookies and similar tracking technologies to track the
            activity on Our Service and store certain information. Tracking
            technologies used are beacons, tags, and scripts to collect and
            track information and to improve and analyze Our Service. The
            technologies We use may include:
          </P>
          <UL>
            <li>
              <Term>Cookies or Browser Cookies.</Term> A cookie is a small file
              placed on Your Device. You can instruct Your browser to refuse
              all Cookies or to indicate when a Cookie is being sent. However,
              if You do not accept Cookies, You may not be able to use some
              parts of our Service. Unless you have adjusted Your browser
              setting so that it will refuse Cookies, our Service may use
              Cookies.
            </li>
            <li>
              <Term>Web Beacons.</Term> Certain sections of our Service and our
              emails may contain small electronic files known as web beacons
              (also referred to as clear gifs, pixel tags, and single-pixel
              gifs) that permit the Company, for example, to count users who
              have visited those pages or opened an email and for other related
              website statistics (for example, recording the popularity of a
              certain section and verifying system and server integrity).
            </li>
          </UL>
          <P>
            Cookies can be "Persistent" or "Session" Cookies. Persistent
            Cookies remain on Your personal computer or mobile device when You
            go offline, while Session Cookies are deleted as soon as You close
            Your web browser. You can learn more about cookies on TermsFeed
            website article.
          </P>
          <P>
            We use both Session and Persistent Cookies for the purposes set out
            below:
          </P>

          <div className="mt-6 space-y-5">
            <div>
              <p className="font-serif text-3xl text-ink">
                Necessary / Essential Cookies
              </p>
              <p className="mt-1 text-[13px] text-olive">
                <Term>Type:</Term> Session Cookies &middot; <Term>Administered by:</Term> Us
              </p>
              <P>
                <Term>Purpose:</Term> These Cookies are essential to provide
                You with services available through the Website and to enable
                You to use some of its features. They help to authenticate
                users and prevent fraudulent use of user accounts. Without
                these Cookies, the services that You have asked for cannot be
                provided, and We only use these Cookies to provide You with
                those services.
              </P>
            </div>
            <div>
              <p className="font-serif text-3xl text-ink">
                Cookies Policy / Notice Acceptance Cookies
              </p>
              <p className="mt-1 text-[13px] text-olive">
                <Term>Type:</Term> Persistent Cookies &middot; <Term>Administered by:</Term> Us
              </p>
              <P>
                <Term>Purpose:</Term> These Cookies identify if users have
                accepted the use of cookies on the Website.
              </P>
            </div>
            <div>
              <p className="font-serif text-3xl text-ink">Functionality Cookies</p>
              <p className="mt-1 text-[13px] text-olive">
                <Term>Type:</Term> Persistent Cookies &middot; <Term>Administered by:</Term> Us
              </p>
              <P>
                <Term>Purpose:</Term> These Cookies allow us to remember
                choices You make when You use the Website, such as remembering
                your login details or language preference. The purpose of
                these Cookies is to provide You with a more personal
                experience and to avoid You having to re-enter your
                preferences every time You use the Website.
              </P>
            </div>
            <div data-testid="privacy-analytics-cookies">
              <p className="font-serif text-3xl text-ink">Analytics Cookies</p>
              <p className="mt-1 text-[13px] text-olive">
                <Term>Type:</Term> Persistent Cookies &middot; <Term>Administered by:</Term> Google LLC
              </p>
              <P>
                <Term>Purpose:</Term> These Cookies are set by Google Analytics 4
                to measure how visitors use the Website (pages viewed, time
                spent, scroll depth, click events) so We can improve it. See
                &ldquo;Analytics: Google Analytics 4&rdquo; below for full
                details and how to opt out.
              </P>
            </div>
          </div>
          <P>
            For more information about the cookies we use and your choices
            regarding cookies, please visit our Cookies Policy or the Cookies
            section of our Privacy Policy.
          </P>

          <H3 id="google-analytics">Analytics: Google Analytics 4</H3>
          <p className="mt-4 text-base leading-relaxed text-ink/85" data-testid="privacy-google-analytics">
            We use Google Analytics 4, provided by Google LLC
            (&ldquo;Google&rdquo;), to analyze how visitors interact with this
            website. Google Analytics 4 uses cookies and other tracking
            technologies to collect information about your visits, including
            pages viewed, time spent, scroll depth, and click events. This
            information is used to compile reports about website activity and
            to help us improve the website. The data collected is processed by
            Google on our behalf and may be transferred to and stored on Google
            servers in the United States. We have configured Google Analytics 4
            to anonymize IP addresses before transmission where technically
            supported. If you are located in a jurisdiction where consent is
            required before analytics processing, Google Analytics 4 will only
            run after you have provided explicit consent through our consent
            management platform. You may opt out of Google Analytics tracking
            at any time by using the{" "}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noreferrer"
              className="underline decoration-ink/30 underline-offset-2 hover:text-ink"
              data-testid="privacy-ga-optout-link"
            >
              Google Analytics Opt-out Browser Add-on
            </a>{" "}
            or by adjusting your preferences in our{" "}
            <button
              type="button"
              onClick={openPreferences}
              className="underline decoration-ink/30 underline-offset-2 hover:text-ink"
              data-testid="privacy-consent-center-link"
            >
              consent center
            </button>.
          </p>

          <H3>Use of Your Personal Data</H3>
          <P>The Company may use Personal Data for the following purposes:</P>
          <UL>
            <li>
              <Term>To provide and maintain our Service,</Term> including to
              monitor the usage of our Service.
            </li>
            <li>
              <Term>To manage Your Account:</Term> to manage Your registration
              as a user of the Service. The Personal Data You provide can give
              You access to different functionalities of the Service that are
              available to You as a registered user.
            </li>
            <li>
              <Term>For the performance of a contract:</Term> the development,
              compliance and undertaking of the purchase contract for the
              products, items or services You have purchased or of any other
              contract with Us through the Service.
            </li>
            <li>
              <Term>To contact You:</Term> To contact You by email, telephone
              calls, SMS, or other equivalent forms of electronic communication,
              such as a mobile application's push notifications regarding
              updates or informative communications related to the
              functionalities, products or contracted services, including the
              security updates, when necessary or reasonable for their
              implementation.
            </li>
            <li>
              <Term>To provide You with news, special offers</Term> and general
              information about other goods, services and events which we offer
              that are similar to those that you have already purchased or
              enquired about unless You have opted not to receive such
              information.
            </li>
            <li>
              <Term>To manage Your requests:</Term> To attend and manage Your
              requests to Us.
            </li>
            <li>
              <Term>For business transfers:</Term> We may use Your information
              to evaluate or conduct a merger, divestiture, restructuring,
              reorganization, dissolution, or other sale or transfer of some or
              all of Our assets, whether as a going concern or as part of
              bankruptcy, liquidation, or similar proceeding, in which Personal
              Data held by Us about our Service users is among the assets
              transferred.
            </li>
            <li>
              <Term>For other purposes:</Term> We may use Your information for
              other purposes, such as data analysis, identifying usage trends,
              determining the effectiveness of our promotional campaigns and to
              evaluate and improve our Service, products, services, marketing
              and your experience.
            </li>
          </UL>
          <P>We may share Your personal information in the following situations:</P>
          <UL>
            <li>
              <Term>With Service Providers:</Term> We may share Your personal
              information with Service Providers to monitor and analyze the use
              of our Service, to contact You.
            </li>
            <li>
              <Term>For business transfers:</Term> We may share or transfer
              Your personal information in connection with, or during
              negotiations of, any merger, sale of Company assets, financing,
              or acquisition of all or a portion of Our business to another
              company.
            </li>
            <li>
              <Term>With Affiliates:</Term> We may share Your information with
              Our affiliates, in which case we will require those affiliates to
              honor this Privacy Policy. Affiliates include Our parent company
              and any other subsidiaries, joint venture partners or other
              companies that We control or that are under common control with
              Us.
            </li>
            <li>
              <Term>With business partners:</Term> We may share Your
              information with Our business partners to offer You certain
              products, services or promotions.
            </li>
            <li>
              <Term>With other users:</Term> when You share personal
              information or otherwise interact in the public areas with other
              users, such information may be viewed by all users and may be
              publicly distributed outside.
            </li>
            <li>
              <Term>With Your consent:</Term> We may disclose Your personal
              information for any other purpose with Your consent.
            </li>
          </UL>

          <H3>Retention of Your Personal Data</H3>
          <P>
            The Company will retain Your Personal Data only for as long as is
            necessary for the purposes set out in this Privacy Policy. We will
            retain and use Your Personal Data to the extent necessary to comply
            with our legal obligations (for example, if we are required to
            retain your data to comply with applicable laws), resolve disputes,
            and enforce our legal agreements and policies.
          </P>
          <P>
            The Company will also retain Usage Data for internal analysis
            purposes. Usage Data is generally retained for a shorter period of
            time, except when this data is used to strengthen the security or
            to improve the functionality of Our Service, or We are legally
            obligated to retain this data for longer time periods.
          </P>

          <H3>Transfer of Your Personal Data</H3>
          <P>
            Your information, including Personal Data, is processed at the
            Company's operating offices and in any other places where the
            parties involved in the processing are located. It means that this
            information may be transferred to &mdash; and maintained on &mdash;
            computers located outside of Your state, province, country or other
            governmental jurisdiction where the data protection laws may differ
            than those from Your jurisdiction.
          </P>
          <P>
            Your consent to this Privacy Policy followed by Your submission of
            such information represents Your agreement to that transfer.
          </P>
          <P>
            The Company will take all steps reasonably necessary to ensure that
            Your data is treated securely and in accordance with this Privacy
            Policy and no transfer of Your Personal Data will take place to an
            organization or a country unless there are adequate controls in
            place including the security of Your data and other personal
            information.
          </P>

          <H3>Delete Your Personal Data</H3>
          <P>
            You have the right to delete or request that We assist in deleting
            the Personal Data that We have collected about You.
          </P>
          <P>
            Our Service may give You the ability to delete certain information
            about You from within the Service.
          </P>
          <P>
            You may update, amend, or delete Your information at any time by
            signing in to Your Account, if you have one, and visiting the
            account settings section that allows you to manage Your personal
            information. You may also contact Us to request access to, correct,
            or delete any personal information that You have provided to Us.
          </P>
          <P>
            Please note, however, that We may need to retain certain
            information when we have a legal obligation or lawful basis to do
            so.
          </P>

          <H3>Disclosure of Your Personal Data</H3>

          <p className="mt-6 font-serif text-3xl leading-snug text-ink">
            Business Transactions
          </p>
          <P>
            If the Company is involved in a merger, acquisition or asset sale,
            Your Personal Data may be transferred. We will provide notice
            before Your Personal Data is transferred and becomes subject to a
            different Privacy Policy.
          </P>

          <p className="mt-8 font-serif text-3xl leading-snug text-ink">
            Law enforcement
          </p>
          <P>
            Under certain circumstances, the Company may be required to
            disclose Your Personal Data if required to do so by law or in
            response to valid requests by public authorities (e.g. a court or a
            government agency).
          </P>

          <p className="mt-8 font-serif text-3xl leading-snug text-ink">
            Other legal requirements
          </p>
          <P>
            The Company may disclose Your Personal Data in the good faith
            belief that such action is necessary to:
          </P>
          <UL>
            <li>Comply with a legal obligation</li>
            <li>Protect and defend the rights or property of the Company</li>
            <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
            <li>Protect the personal safety of Users of the Service or the public</li>
            <li>Protect against legal liability</li>
          </UL>

          <H3>Security of Your Personal Data</H3>
          <P>
            The security of Your Personal Data is important to Us, but remember
            that no method of transmission over the Internet, or method of
            electronic storage is 100% secure. While We strive to use
            commercially acceptable means to protect Your Personal Data, We
            cannot guarantee its absolute security.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="gdpr-privacy">GDPR Privacy</H2>

          <H3>Legal Basis for Processing Personal Data Under GDPR</H3>
          <P>We may process Personal Data under the following conditions:</P>
          <UL>
            <li>
              <Term>Consent:</Term> You have given Your consent for processing
              Personal Data for one or more specific purposes.
            </li>
            <li>
              <Term>Performance of a contract:</Term> Provision of Personal
              Data is necessary for the performance of an agreement with You
              and/or for any pre-contractual obligations thereof.
            </li>
            <li>
              <Term>Legal obligations:</Term> Processing Personal Data is
              necessary for compliance with a legal obligation to which the
              Company is subject.
            </li>
            <li>
              <Term>Vital interests:</Term> Processing Personal Data is
              necessary in order to protect Your vital interests or of another
              natural person.
            </li>
            <li>
              <Term>Public interests:</Term> Processing Personal Data is
              related to a task that is carried out in the public interest or
              in the exercise of official authority vested in the Company.
            </li>
            <li>
              <Term>Legitimate interests:</Term> Processing Personal Data is
              necessary for the purposes of the legitimate interests pursued by
              the Company.
            </li>
          </UL>
          <P>
            In any case, the Company will gladly help to clarify the specific
            legal basis that applies to the processing, and in particular
            whether the provision of Personal Data is a statutory or
            contractual requirement, or a requirement necessary to enter into a
            contract.
          </P>

          <H3>Your Rights Under the GDPR</H3>
          <P>
            The Company undertakes to respect the confidentiality of Your
            Personal Data and to guarantee You can exercise Your rights.
          </P>
          <P>
            You have the right under this Privacy Policy, and by law if You are
            within the EU, to:
          </P>
          <UL>
            <li>
              <Term>Request access to Your Personal Data.</Term> The right to
              access, update or delete the information We have on You. Whenever
              made possible, you can access, update or request deletion of Your
              Personal Data directly within Your account settings section. If
              you are unable to perform these actions yourself, please contact
              Us to assist You. This also enables You to receive a copy of the
              Personal Data We hold about You.
            </li>
            <li>
              <Term>Request correction of the Personal Data</Term> that We hold
              about You. You have the right to have any incomplete or
              inaccurate information We hold about You corrected.
            </li>
            <li>
              <Term>Object to processing of Your Personal Data.</Term> This
              right exists where We are relying on a legitimate interest as the
              legal basis for Our processing and there is something about Your
              particular situation, which makes You want to object to our
              processing of Your Personal Data on this ground. You also have
              the right to object where We are processing Your Personal Data
              for direct marketing purposes.
            </li>
            <li>
              <Term>Request erasure of Your Personal Data.</Term> You have the
              right to ask Us to delete or remove Personal Data when there is
              no good reason for Us to continue processing it.
            </li>
            <li>
              <Term>Request the transfer of Your Personal Data.</Term> We will
              provide to You, or to a third-party You have chosen, Your
              Personal Data in a structured, commonly used, machine-readable
              format. Please note that this right only applies to automated
              information which You initially provided consent for Us to use or
              where We used the information to perform a contract with You.
            </li>
            <li>
              <Term>Withdraw Your consent.</Term> You have the right to
              withdraw Your consent on using your Personal Data. If You
              withdraw Your consent, We may not be able to provide You with
              access to certain specific functionalities of the Service.
            </li>
          </UL>

          <H3>Exercising of Your GDPR Data Protection Rights</H3>
          <P>
            You may exercise Your rights of access, rectification, cancellation
            and opposition by contacting Us. Please note that we may ask You to
            verify Your identity before responding to such requests. If You
            make a request, We will try our best to respond to You as soon as
            possible.
          </P>
          <P>
            You have the right to complain to a Data Protection Authority about
            Our collection and use of Your Personal Data. For more information,
            if You are in the European Economic Area (EEA), please contact Your
            local data protection authority in the EEA.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="ccpa-cpra-privacy-notice">CCPA/CPRA Privacy Notice</H2>
          <P>
            This privacy notice section for California residents supplements
            the information contained in Our Privacy Policy and it applies
            solely to all visitors, users, and others who reside in the State
            of California.
          </P>

          <H3>Categories of Personal Information Collected</H3>
          <P>
            We collect information that identifies, relates to, describes,
            references, is capable of being associated with, or could
            reasonably be linked, directly or indirectly, with a particular
            Consumer or Device. The following is a list of categories of
            personal information which we may collect or may have been
            collected from California residents within the last twelve (12)
            months.
          </P>
          <P>
            Please note that the categories and examples provided in the list
            below are those defined in the CCPA/CPRA. This does not mean that
            all examples of that category of personal information were in fact
            collected by Us, but reflects our good faith belief to the best of
            Our knowledge that some of that information from the applicable
            category may be and may have been collected. For example, certain
            categories of personal information would only be collected if You
            provided such personal information directly to Us.
          </P>

          <Category
            label="Category A: Identifiers"
            examples="A real name, alias, postal address, unique personal identifier, online identifier, Internet Protocol address, email address, account name, driver's license number, passport number, or other similar identifiers."
            collected="Yes"
          />
          <Category
            label="Category B: Personal information categories listed in the California Customer Records statute (Cal. Civ. Code § 1798.80(e))."
            examples="A name, signature, Social Security number, physical characteristics or description, address, telephone number, passport number, driver's license or state identification card number, insurance policy number, education, employment, employment history, bank account number, credit card number, debit card number, or any other financial information, medical information, or health insurance information. Some personal information included in this category may overlap with other categories."
            collected="Yes"
          />
          <Category
            label="Category C: Protected classification characteristics under California or federal law."
            examples="Age (40 years or older), race, color, ancestry, national origin, citizenship, religion or creed, marital status, medical condition, physical or mental disability, sex (including gender, gender identity, gender expression, pregnancy or childbirth and related medical conditions), sexual orientation, veteran or military status, genetic information (including familial genetic information)."
            collected="No"
          />
          <Category
            label="Category D: Commercial information."
            examples="Records and history of products or services purchased or considered."
            collected="No"
          />
          <Category
            label="Category E: Biometric information."
            examples="Genetic, physiological, behavioral, and biological characteristics, or activity patterns used to extract a template or other identifier or identifying information, such as, fingerprints, faceprints, and voiceprints, iris or retina scans, keystroke, gait, or other physical patterns, and sleep, health, or exercise data."
            collected="No"
          />
          <Category
            label="Category F: Internet or other similar network activity."
            examples="Interaction with our Service or advertisement."
            collected="Yes"
          />
          <Category
            label="Category G: Geolocation data."
            examples="Approximate physical location."
            collected="No"
          />
          <Category
            label="Category H: Sensory data."
            examples="Audio, electronic, visual, thermal, olfactory, or similar information."
            collected="No"
          />
          <Category
            label="Category I: Professional or employment-related information."
            examples="Current or past job history or performance evaluations."
            collected="No"
          />
          <Category
            label="Category J: Non-public education information (per the Family Educational Rights and Privacy Act (20 U.S.C. Section 1232g, 34 C.F.R. Part 99))."
            examples="Education records directly related to a student maintained by an educational institution or party acting on its behalf, such as grades, transcripts, class lists, student schedules, student identification codes, student financial information, or student disciplinary records."
            collected="No"
          />
          <Category
            label="Category K: Inferences drawn from other personal information."
            examples="Profile reflecting a person's preferences, characteristics, psychological trends, predispositions, behavior, attitudes, intelligence, abilities, and aptitudes."
            collected="No"
          />
          <Category
            label="Category L: Sensitive personal information."
            examples="Account login and password information, geolocation data."
            collected="Yes"
          />

          <P>Under CCPA/CPRA, personal information does not include:</P>
          <UL>
            <li>Publicly available information from government records</li>
            <li>Deidentified or aggregated consumer information</li>
            <li>
              Information excluded from the CCPA/CPRA's scope, such as:
              <ul className="mt-2 list-[circle] space-y-2 pl-6 marker:text-tangerine">
                <li>
                  Health or medical information covered by the Health Insurance
                  Portability and Accountability Act of 1996 (HIPAA) and the
                  California Confidentiality of Medical Information Act (CMIA)
                  or clinical trial data
                </li>
                <li>
                  Personal Information covered by certain sector-specific
                  privacy laws, including the Fair Credit Reporting Act (FRCA),
                  the Gramm-Leach-Bliley Act (GLBA) or California Financial
                  Information Privacy Act (FIPA), and the Driver's Privacy
                  Protection Act of 1994
                </li>
              </ul>
            </li>
          </UL>

          <H3>Sources of Personal Information</H3>
          <P>
            We obtain the categories of personal information listed above from
            the following categories of sources:
          </P>
          <UL>
            <li>
              <Term>Directly from You.</Term> For example, from the forms You
              complete on our Service, preferences You express or provide
              through our Service.
            </li>
            <li>
              <Term>Indirectly from You.</Term> For example, from observing
              Your activity on our Service.
            </li>
            <li>
              <Term>Automatically from You.</Term> For example, through cookies
              We or our Service Providers set on Your Device as You navigate
              through our Service.
            </li>
            <li>
              <Term>From Service Providers.</Term> For example, or other
              third-party vendors that We use to provide the Service to You.
            </li>
          </UL>

          <H3>Use of Personal Information</H3>
          <P>
            We may use or disclose personal information We collect for
            "business purposes" or "commercial purposes" (as defined under the
            CCPA/CPRA), which may include the following examples:
          </P>
          <UL>
            <li>To operate our Service and provide You with Our Service.</li>
            <li>
              To provide You with support and to respond to Your inquiries,
              including to investigate and address Your concerns and monitor
              and improve our Service.
            </li>
            <li>
              To fulfill or meet the reason You provided the information. For
              example, if You share Your contact information to ask a question
              about our Service, We will use that personal information to
              respond to Your inquiry.
            </li>
            <li>
              To respond to law enforcement requests and as required by
              applicable law, court order, or governmental regulations.
            </li>
            <li>
              As described to You when collecting Your personal information or
              as otherwise set forth in the CCPA/CPRA.
            </li>
            <li>For internal administrative and auditing purposes.</li>
            <li>
              To detect security incidents and protect against malicious,
              deceptive, fraudulent or illegal activity, including, when
              necessary, to prosecute those responsible for such activities.
            </li>
            <li>Other one-time uses.</li>
          </UL>
          <P>
            Please note that the examples provided above are illustrative and
            not intended to be exhaustive. For more details on how we use this
            information, please refer to the "Use of Your Personal Data"
            section.
          </P>
          <P>
            If We decide to collect additional categories of personal
            information or use the personal information We collected for
            materially different, unrelated, or incompatible purposes We will
            update this Privacy Policy.
          </P>

          <H3>Disclosure of Personal Information</H3>
          <P>
            We may use or disclose and may have used or disclosed in the last
            twelve (12) months the following categories of personal information
            for business or commercial purposes:
          </P>
          <UL>
            <li>Category A: Identifiers</li>
            <li>
              Category B: Personal information categories listed in the
              California Customer Records statute (Cal. Civ. Code § 1798.80(e))
            </li>
            <li>Category F: Internet or other similar network activity</li>
          </UL>
          <P>
            Please note that the categories listed above are those defined in
            the CCPA/CPRA. This does not mean that all examples of that
            category of personal information were in fact disclosed, but
            reflects our good faith belief to the best of our knowledge that
            some of that information from the applicable category may be and
            may have been disclosed.
          </P>
          <P>
            When We disclose personal information for a business purpose or a
            commercial purpose, We enter a contract that describes the purpose
            and requires the recipient to both keep that personal information
            confidential and not use it for any purpose except performing the
            contract.
          </P>

          <H3>Share of Personal Information</H3>
          <P>
            We may share, and have shared in the last twelve (12) months, Your
            personal information identified in the above categories with the
            following categories of third parties:
          </P>
          <UL>
            <li>Service Providers</li>
            <li>Our affiliates</li>
            <li>Our business partners</li>
            <li>
              Third party vendors to whom You or Your agents authorize Us to
              disclose Your personal information in connection with products or
              services We provide to You
            </li>
          </UL>

          <H3>Sale of Personal Information</H3>
          <P>
            As defined in the CCPA/CPRA, "sell" and "sale" mean selling,
            renting, releasing, disclosing, disseminating, making available,
            transferring, or otherwise communicating orally, in writing, or by
            electronic or other means, a Consumer's personal information by
            the Business to a third party for valuable consideration. This
            means that We may have received some kind of benefit in return for
            sharing personal information, but not necessarily a monetary
            benefit.
          </P>
          <P>
            We do not sell personal information as the term sell is commonly
            understood. We do allow Service Providers to use Your personal
            information for the business purposes described in Our Privacy
            Policy, and these may be deemed a sale under CCPA/CPRA.
          </P>
          <P>
            We may sell and may have sold in the last twelve (12) months the
            following categories of personal information:
          </P>
          <UL>
            <li>Category A: Identifiers</li>
            <li>
              Category B: Personal information categories listed in the
              California Customer Records statute (Cal. Civ. Code § 1798.80(e))
            </li>
            <li>Category F: Internet or other similar network activity</li>
          </UL>
          <P>
            Please note that the categories listed above are those defined in
            the CCPA/CPRA. This does not mean that all examples of that
            category of personal information were in fact sold, but reflects
            our good faith belief to the best of Our knowledge that some of
            that information from the applicable category may be and may have
            been shared for value in return.
          </P>

          <H3>Sale of Personal Information of Minors Under 16 Years of Age</H3>
          <P>
            We do not knowingly collect personal information from minors under
            the age of 16 through our Service, although certain third party
            websites that we link to may do so. These third-party websites have
            their own terms of use and privacy policies and We encourage
            parents and legal guardians to monitor their children's Internet
            usage and instruct their children to never provide information on
            other websites without their permission.
          </P>
          <P>
            We do not sell the personal information of Consumers We actually
            know are less than 16 years of age, unless We receive affirmative
            authorization (the "right to opt-in") from either the Consumer who
            is between 13 and 16 years of age, or the parent or guardian of a
            Consumer less than 13 years of age. Consumers who opt-in to the
            sale of personal information may opt-out of future sales at any
            time. To exercise the right to opt-out, You (or Your authorized
            representative) may submit a request to Us by contacting Us.
          </P>
          <P>
            If You have reason to believe that a child under the age of 13 (or
            16) has provided Us with personal information, please contact Us
            with sufficient detail to enable Us to delete that information.
          </P>

          <H3>Your Rights Under the CCPA/CPRA</H3>
          <P>
            The CCPA/CPRA provides California residents with specific rights
            regarding their personal information. If You are a resident of
            California, You have the following rights:
          </P>
          <UL>
            <li>
              <Term>The right to notice.</Term> You have the right to be
              notified which categories of Personal Data are being collected
              and the purposes for which the Personal Data is being used.
            </li>
            <li>
              <Term>The right to know/access.</Term> Under CCPA/CPRA, You have
              the right to request that We disclose information to You about
              Our collection, use, sale, disclosure for business purposes and
              share of personal information. Once We receive and confirm Your
              request, We will disclose to You:
              <ul className="mt-2 list-[circle] space-y-2 pl-6 marker:text-tangerine">
                <li>The categories of personal information We collected about You</li>
                <li>The categories of sources for the personal information We collected about You</li>
                <li>Our business or commercial purposes for collecting or selling that personal information</li>
                <li>The categories of third parties with whom We share that personal information</li>
                <li>The specific pieces of personal information We collected about You</li>
                <li>
                  If we sold Your personal information or disclosed Your
                  personal information for a business purpose, We will disclose
                  to You:
                  <ul className="mt-2 list-[square] space-y-2 pl-6 marker:text-cobalt">
                    <li>The categories of personal information categories sold</li>
                    <li>The categories of personal information categories disclosed</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>
              <Term>The right to say no to the sale or sharing of Personal Data (opt-out).</Term>{" "}
              You have the right to direct Us to not sell Your personal
              information. To submit an opt-out request, please see the "Do Not
              Sell My Personal Information" section or contact Us.
            </li>
            <li>
              <Term>The right to correct Personal Data.</Term> You have the
              right to correct or rectify any any inaccurate personal
              information about You that We collected. Once We receive and
              confirm Your request, We will use commercially reasonable efforts
              to correct (and direct our Service Providers to correct) Your
              personal information, unless an exception applies.
            </li>
            <li>
              <Term>The right to limit use and disclosure of sensitive Personal Data.</Term>{" "}
              You have the right to request to limit the use or disclosure of
              certain sensitive personal information We collected about You,
              unless an exception applies. To submit, please see the "Limit the
              Use or Disclosure of My Sensitive Personal Information" section
              or contact Us.
            </li>
            <li>
              <Term>The right to delete Personal Data.</Term> You have the
              right to request the deletion of Your Personal Data under certain
              circumstances, subject to certain exceptions. Once We receive and
              confirm Your request, We will delete (and direct Our Service
              Providers to delete) Your personal information from our records,
              unless an exception applies. We may deny Your deletion request if
              retaining the information is necessary for Us or Our Service
              Providers to:
              <ul className="mt-2 list-[circle] space-y-2 pl-6 marker:text-tangerine">
                <li>
                  Complete the transaction for which We collected the personal
                  information, provide a good or service that You requested,
                  take actions reasonably anticipated within the context of our
                  ongoing business relationship with You, or otherwise perform
                  our contract with You.
                </li>
                <li>
                  Detect security incidents, protect against malicious,
                  deceptive, fraudulent, or illegal activity, or prosecute
                  those responsible for such activities.
                </li>
                <li>
                  Debug products to identify and repair errors that impair
                  existing intended functionality.
                </li>
                <li>
                  Exercise free speech, ensure the right of another consumer to
                  exercise their free speech rights, or exercise another right
                  provided for by law.
                </li>
                <li>
                  Comply with the California Electronic Communications Privacy
                  Act (Cal. Penal Code § 1546 et. seq.).
                </li>
                <li>
                  Engage in public or peer-reviewed scientific, historical, or
                  statistical research in the public interest that adheres to
                  all other applicable ethics and privacy laws, when the
                  information's deletion may likely render impossible or
                  seriously impair the research's achievement, if You
                  previously provided informed consent.
                </li>
                <li>
                  Enable solely internal uses that are reasonably aligned with
                  consumer expectations based on Your relationship with Us.
                </li>
                <li>Comply with a legal obligation.</li>
                <li>
                  Make other internal and lawful uses of that information that
                  are compatible with the context in which You provided it.
                </li>
              </ul>
            </li>
            <li>
              <Term>The right not to be discriminated against.</Term> You have
              the right not to be discriminated against for exercising any of
              Your consumer's rights, including by:
              <ul className="mt-2 list-[circle] space-y-2 pl-6 marker:text-tangerine">
                <li>Denying goods or services to You</li>
                <li>
                  Charging different prices or rates for goods or services,
                  including the use of discounts or other benefits or imposing
                  penalties
                </li>
                <li>Providing a different level or quality of goods or services to You</li>
                <li>
                  Suggesting that You will receive a different price or rate
                  for goods or services or a different level or quality of
                  goods or services
                </li>
              </ul>
            </li>
          </UL>

          <H3>Exercising Your CCPA/CPRA Data Protection Rights</H3>
          <P>
            Please see the "Do Not Sell My Personal Information" section and
            "Limit the Use or Disclosure of My Sensitive Personal Information"
            section for more information on how to opt out and limit the use of
            sensitive information collected.
          </P>
          <P>
            Additionally, in order to exercise any of Your rights under the
            CCPA/CPRA, and if You are a California resident, You can contact
            Us:
          </P>
          <UL>
            <li>
              By visiting this page on our website:{" "}
              <a
                href="http://www.myrichaunt.org/contact"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-tangerine underline underline-offset-4 hover:text-cobalt"
              >
                http://www.myrichaunt.org/contact
              </a>
            </li>
          </UL>
          <P>
            Only You, or a person registered with the California Secretary of
            State that You authorize to act on Your behalf, may make a
            verifiable request related to Your personal information.
          </P>
          <P>Your request to Us must:</P>
          <UL>
            <li>
              Provide sufficient information that allows Us to reasonably
              verify You are the person about whom We collected personal
              information or an authorized representative
            </li>
            <li>
              Describe Your request with sufficient detail that allows Us to
              properly understand, evaluate, and respond to it
            </li>
          </UL>
          <P>We cannot respond to Your request or provide You with the required information if We cannot:</P>
          <UL>
            <li>Verify Your identity or authority to make the request</li>
            <li>And confirm that the personal information relates to You</li>
          </UL>
          <P>
            We will disclose and deliver the required information free of
            charge within 45 days of receiving Your verifiable request. The
            time period to provide the required information may be extended
            once by an additional 45 days when reasonably necessary and with
            prior notice.
          </P>
          <P>
            Any disclosures We provide will only cover the 12-month period
            preceding the verifiable request's receipt.
          </P>
          <P>
            For data portability requests, We will select a format to provide
            Your personal information that is readily usable and should allow
            You to transmit the information from one entity to another entity
            without hindrance.
          </P>

          <H3>Do Not Sell My Personal Information</H3>
          <P>
            As defined in the CCPA/CPRA, "sell" and "sale" mean selling,
            renting, releasing, disclosing, disseminating, making available,
            transferring, or otherwise communicating orally, in writing, or by
            electronic or other means, a Consumer's personal information by
            the Business to a third party for valuable consideration. This
            means that We may have received some kind of benefit in return for
            sharing personal information, but not necessarily a monetary
            benefit.
          </P>
          <P>
            We do not sell personal information as the term sell is commonly
            understood. We do allow Service Providers to use Your personal
            information for the business purposes described in Our Privacy
            Policy, and these may be deemed a sale under CCPA/CPRA.
          </P>
          <P>
            You have the right to opt-out of the sale of Your personal
            information. Once We receive and confirm a verifiable consumer
            request from You, we will stop selling Your personal information.
            To exercise Your right to opt-out, please contact Us.
          </P>

          <H3>Limit the Use or Disclosure of My Sensitive Personal Information</H3>
          <P>
            If You are a California resident, You have the right to limit the
            use and disclosure of Your sensitive personal information to that
            use which is necessary to perform the services or provide the goods
            reasonably expected by an average Consumer who requests such
            services or goods.
          </P>
          <P>
            We collect, use and disclose sensitive personal information in ways
            that are necessary to provide the Service. For more information on
            how We use Your personal information, please see the "Use of Your
            Personal Data" section or contact us.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="do-not-track-policy">
            "Do Not Track" Policy as Required by California Online Privacy
            Protection Act (CalOPPA)
          </H2>
          <P>Our Service does not respond to Do Not Track signals.</P>
          <P>
            However, some third party websites do keep track of Your browsing
            activities. If You are visiting such websites, You can set Your
            preferences in Your web browser to inform websites that You do not
            want to be tracked. You can enable or disable DNT by visiting the
            preferences or settings page of Your web browser.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="california-shine-the-light">
            Your California Privacy Rights (California's Shine the Light law)
          </H2>
          <P>
            Under California Civil Code Section 1798 (California's Shine the
            Light law), California residents with an established business
            relationship with us can request information once a year about
            sharing their Personal Data with third parties for the third
            parties' direct marketing purposes.
          </P>
          <P>
            If you'd like to request more information under the California
            Shine the Light law, and if You are a California resident, You can
            contact Us using the contact information provided below.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="california-privacy-minors">
            California Privacy Rights for Minor Users (California Business and
            Professions Code Section 22581)
          </H2>
          <P>
            California Business and Professions Code Section 22581 allows
            California residents under the age of 18 who are registered users
            of online sites, services or applications to request and obtain
            removal of content or information they have publicly posted.
          </P>
          <P>
            To request removal of such data, and if You are a California
            resident, You can contact Us using the contact information provided
            below, and include the email address associated with Your account.
          </P>
          <P>
            Be aware that Your request does not guarantee complete or
            comprehensive removal of content or information posted online and
            that the law may not permit or require removal in certain
            circumstances.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="childrens-privacy">Children's Privacy</H2>
          <P>
            Our Service does not address anyone under the age of 13. We do not
            knowingly collect personally identifiable information from anyone
            under the age of 13. If You are a parent or guardian and You are
            aware that Your child has provided Us with Personal Data, please
            contact Us. If We become aware that We have collected Personal Data
            from anyone under the age of 13 without verification of parental
            consent, We take steps to remove that information from Our servers.
          </P>
          <P>
            If We need to rely on consent as a legal basis for processing Your
            information and Your country requires consent from a parent, We may
            require Your parent's consent before We collect and use that
            information.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="links-to-other-websites">Links to Other Websites</H2>
          <P>
            Our Service may contain links to other websites that are not
            operated by Us. If You click on a third party link, You will be
            directed to that third party's site. We strongly advise You to
            review the Privacy Policy of every site You visit.
          </P>
          <P>
            We have no control over and assume no responsibility for the
            content, privacy policies or practices of any third party sites or
            services.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="changes-to-this-privacy-policy">Changes to This Privacy Policy</H2>
          <P>
            We may update Our Privacy Policy from time to time. We will notify
            You of any changes by posting the new Privacy Policy on this page.
          </P>
          <P>
            We will let You know via email and/or a prominent notice on Our
            Service, prior to the change becoming effective and update the
            "Last updated" date at the top of this Privacy Policy.
          </P>
          <P>
            You are advised to review this Privacy Policy periodically for any
            changes. Changes to this Privacy Policy are effective when they are
            posted on this page.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="contact-us">Contact Us</H2>
          <P>
            If you have any questions about this Privacy Policy, You can
            contact us:
          </P>
          <UL>
            <li>
              By visiting this page on our website:{" "}
              <a
                href="http://www.myrichaunt.org/contact"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-tangerine underline underline-offset-4 hover:text-cobalt"
              >
                http://www.myrichaunt.org/contact
              </a>
            </li>
          </UL>
        </FadeUp>
        </article>
      </main>

      <Footer variant="minimal" />

      <CreateCampaign open={createOpen} onOpenChange={setCreateOpen} />
      <DonateComingSoonDialog open={donateOpen} onOpenChange={setDonateOpen} />
    </div>
  );
}
